import React, { useState, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../api';
import ManeLogo from '../components/Logo';

/* ── Design tokens ── */
const BG = '#050810';
const SURFACE = '#0d1117';
const CARD = '#111827';
const BORDER = '#1f2937';
const GOLD = '#D4AF37';
const GOLD_DIM = 'rgba(212,175,55,0.15)';
const WHITE = '#ffffff';
const MUTED = '#9CA3B4';
const DIM = '#6B7280';

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || process.env.REACT_APP_STRIPE_KEY || 'pk_live_51TF3BX5RBDyxtOupr76LTyCbyEmeWaRHHFu3KgZ9GxZHupEX5tAXjon4O4UCg0XxpoSfGiPhjTwN0fxBdg4yy8CF00PFvcKR3J'
);

const STEPS = ['Account', 'Profile', 'Plan', 'Payment', 'Success'];

const PROFESSIONS = ['Barber', 'Stylist', 'Nail Tech', 'Esthetician', 'Coach', 'Other'];

const PLANS = [
  { id: 'starter', name: 'Starter', price: '$29', period: '/mo', desc: 'For solo professionals just getting started', features: ['Professional profile page', 'Online booking', 'Client management', 'Email reminders', 'Basic analytics'] },
  { id: 'pro', name: 'Pro', price: '$59', period: '/mo', badge: 'MOST POPULAR', desc: 'Everything you need to grow your business', features: ['Everything in Starter', 'SMS reminders', 'AI booking assistant', 'CRM & follow-ups', 'Advanced analytics', 'Priority support'] },
  { id: 'agency', name: 'Agency', price: '$45', period: '/seat/mo', desc: 'For teams and multi-location businesses', features: ['Everything in Pro', 'Multi-staff management', 'Custom branding', 'API access', 'Dedicated account manager', 'White-label option'] },
];

const FOUNDING = { id: 'founding', name: 'Founding Member', price: '$199', period: 'one-time', badge: 'LIMITED OFFER', desc: 'Lifetime Pro access — only for early adopters', features: ['Everything in Pro — forever', 'No monthly fees ever', 'Founding Member badge', 'Early access to new features', 'Direct line to founders'] };

/* ── Shared styles ── */
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: MUTED, marginBottom: '6px' };
const inputStyle = { width: '100%', background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: '8px', padding: '14px 16px', color: WHITE, fontSize: '14px', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' };
const btnPrimary = { width: '100%', background: GOLD, color: '#000', fontWeight: 900, fontSize: '14px', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '18px', border: 'none', borderRadius: '8px', cursor: 'pointer', transition: 'opacity 0.2s' };
const btnSecondary = { width: '100%', background: 'transparent', color: MUTED, fontWeight: 700, fontSize: '13px', letterSpacing: '0.05em', padding: '14px', border: `1px solid ${BORDER}`, borderRadius: '8px', cursor: 'pointer' };

/* ── Progress Bar ── */
function ProgressBar({ step }) {
  return (
    <div style={{ padding: '24px 24px 0', maxWidth: '600px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        {STEPS.map((s, i) => (
          <span key={s} style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: i <= step ? GOLD : DIM, transition: 'color 0.3s' }}>{s}</span>
        ))}
      </div>
      <div style={{ height: '3px', background: BORDER, borderRadius: '2px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((step + 1) / STEPS.length) * 100}%`, background: `linear-gradient(90deg, ${GOLD}, #f5d77a)`, borderRadius: '2px', transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
}

/* ── Step 1: Account ── */
function AccountStep({ form, onChange, onNext, error }) {
  return (
    <div>
      <h2 style={{ fontWeight: 900, fontSize: '24px', marginBottom: '8px', textAlign: 'center' }}>Create Your Account</h2>
      <p style={{ color: MUTED, fontSize: '14px', textAlign: 'center', marginBottom: '32px' }}>Start building your professional presence</p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        <div>
          <label style={labelStyle}>First Name *</label>
          <input style={inputStyle} name="firstName" value={form.firstName} onChange={onChange} placeholder="John" autoComplete="given-name" />
        </div>
        <div>
          <label style={labelStyle}>Last Name *</label>
          <input style={inputStyle} name="lastName" value={form.lastName} onChange={onChange} placeholder="Smith" autoComplete="family-name" />
        </div>
      </div>
      <div style={{ marginBottom: '12px' }}>
        <label style={labelStyle}>Email *</label>
        <input style={inputStyle} name="email" type="email" value={form.email} onChange={onChange} placeholder="you@email.com" autoComplete="email" />
      </div>
      <div style={{ marginBottom: '12px' }}>
        <label style={labelStyle}>Phone</label>
        <input style={inputStyle} name="phone" type="tel" value={form.phone} onChange={onChange} placeholder="(555) 000-0000" autoComplete="tel" />
      </div>
      <div style={{ marginBottom: '12px' }}>
        <label style={labelStyle}>Password *</label>
        <input style={inputStyle} name="password" type="password" value={form.password} onChange={onChange} placeholder="Min 8 characters" autoComplete="new-password" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' }}>
        <div>
          <label style={labelStyle}>Business Name *</label>
          <input style={inputStyle} name="businessName" value={form.businessName} onChange={onChange} placeholder="Your Shop Name" />
        </div>
        <div>
          <label style={labelStyle}>City</label>
          <input style={inputStyle} name="city" value={form.city} onChange={onChange} placeholder="Miami" autoComplete="address-level2" />
        </div>
      </div>

      {error && <ErrorBox msg={error} />}
      <button style={btnPrimary} onClick={onNext}>Continue</button>
    </div>
  );
}

/* ── Step 2: Profile ── */
function ProfileStep({ form, onChange, onNext, onBack }) {
  return (
    <div>
      <h2 style={{ fontWeight: 900, fontSize: '24px', marginBottom: '8px', textAlign: 'center' }}>Your Professional Profile</h2>
      <p style={{ color: MUTED, fontSize: '14px', textAlign: 'center', marginBottom: '32px' }}>Tell us about your expertise</p>

      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>What do you do? *</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
          {PROFESSIONS.map(p => (
            <button
              key={p}
              onClick={() => onChange({ target: { name: 'profession', value: p } })}
              style={{
                padding: '14px 8px', borderRadius: '8px', border: `1px solid ${form.profession === p ? GOLD : BORDER}`,
                background: form.profession === p ? GOLD_DIM : 'transparent', color: form.profession === p ? GOLD : MUTED,
                fontWeight: 700, fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Years of Experience</label>
        <input style={inputStyle} name="yearsExp" type="number" min="0" max="50" value={form.yearsExp} onChange={onChange} placeholder="5" />
      </div>
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Bio <span style={{ color: DIM, fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
        <textarea
          style={{ ...inputStyle, minHeight: '80px', resize: 'vertical', fontFamily: 'inherit' }}
          name="bio" value={form.bio} onChange={onChange}
          placeholder="Tell potential clients about yourself..."
        />
      </div>
      <div style={{ marginBottom: '28px' }}>
        <label style={labelStyle}>Instagram <span style={{ color: DIM, fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
        <input style={inputStyle} name="instagram" value={form.instagram} onChange={onChange} placeholder="@yourhandle" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
        <button style={btnSecondary} onClick={onBack}>Back</button>
        <button style={btnPrimary} onClick={onNext}>Continue</button>
      </div>
    </div>
  );
}

/* ── Step 3: Plan Selection ── */
function PlanStep({ form, onChange, onNext, onBack }) {
  const selectPlan = (id) => onChange({ target: { name: 'plan', value: id } });

  const renderCard = (p, isFounding) => {
    const selected = form.plan === p.id;
    return (
      <div
        key={p.id}
        onClick={() => selectPlan(p.id)}
        style={{
          background: selected ? GOLD_DIM : CARD, border: `2px solid ${selected ? GOLD : BORDER}`,
          borderRadius: '12px', padding: '24px', cursor: 'pointer', transition: 'all 0.25s',
          position: 'relative', overflow: 'hidden',
        }}
      >
        {p.badge && (
          <div style={{
            position: 'absolute', top: '12px', right: '-28px', background: isFounding ? '#ef4444' : GOLD, color: isFounding ? '#fff' : '#000',
            fontSize: '9px', fontWeight: 900, letterSpacing: '0.12em', padding: '4px 32px', transform: 'rotate(45deg)',
          }}>
            {p.badge}
          </div>
        )}
        <h3 style={{ fontWeight: 900, fontSize: '18px', marginBottom: '4px', color: WHITE }}>{p.name}</h3>
        <div style={{ marginBottom: '8px' }}>
          <span style={{ fontSize: '32px', fontWeight: 900, color: GOLD }}>{p.price}</span>
          <span style={{ color: MUTED, fontSize: '14px' }}>{p.period}</span>
        </div>
        <p style={{ color: MUTED, fontSize: '13px', marginBottom: '16px', lineHeight: 1.5 }}>{p.desc}</p>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {p.features.map(f => (
            <li key={f} style={{ color: '#ccc', fontSize: '13px', padding: '4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: GOLD, fontSize: '14px' }}>✓</span> {f}
            </li>
          ))}
        </ul>
        {selected && (
          <div style={{ marginTop: '16px', textAlign: 'center', color: GOLD, fontWeight: 900, fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            ✓ Selected
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <h2 style={{ fontWeight: 900, fontSize: '24px', marginBottom: '8px', textAlign: 'center' }}>Choose Your Plan</h2>
      <p style={{ color: MUTED, fontSize: '14px', textAlign: 'center', marginBottom: '32px' }}>All plans include a 14-day free trial</p>

      {/* Founding offer */}
      <div style={{ marginBottom: '16px' }}>
        {renderCard(FOUNDING, true)}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '28px' }}>
        {PLANS.map(p => renderCard(p, false))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
        <button style={btnSecondary} onClick={onBack}>Back</button>
        <button style={btnPrimary} onClick={onNext}>Continue to Payment</button>
      </div>
    </div>
  );
}

/* ── Step 4: Payment (Stripe) ── */
function PaymentForm({ form, onBack, onSubmit, loading, error }) {
  const stripe = useStripe();
  const elements = useElements();

  const handlePay = async () => {
    if (!stripe || !elements) return;
    const card = elements.getElement(CardElement);
    const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({ type: 'card', card });
    if (pmError) {
      onSubmit(null, pmError.message);
      return;
    }
    onSubmit(paymentMethod.id, null);
  };

  const selectedPlan = [...PLANS, FOUNDING].find(p => p.id === form.plan) || PLANS[0];

  return (
    <div>
      <h2 style={{ fontWeight: 900, fontSize: '24px', marginBottom: '8px', textAlign: 'center' }}>Payment Details</h2>
      <p style={{ color: MUTED, fontSize: '14px', textAlign: 'center', marginBottom: '32px' }}>Secure checkout powered by Stripe</p>

      {/* Order summary */}
      <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <span style={{ fontWeight: 700, color: WHITE }}>{selectedPlan.name}</span>
          <span style={{ fontWeight: 900, color: GOLD, fontSize: '20px' }}>{selectedPlan.price}<span style={{ fontSize: '13px', color: MUTED }}>{selectedPlan.period}</span></span>
        </div>
        <p style={{ color: MUTED, fontSize: '13px', margin: 0 }}>{selectedPlan.desc}</p>
      </div>

      {/* Card input */}
      <div style={{ marginBottom: '24px' }}>
        <label style={labelStyle}>Card Information</label>
        <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: '8px', padding: '16px' }}>
          <CardElement
            options={{
              style: {
                base: { fontSize: '16px', color: WHITE, '::placeholder': { color: DIM }, iconColor: GOLD },
                invalid: { color: '#ef4444', iconColor: '#ef4444' },
              },
            }}
          />
        </div>
      </div>

      {error && <ErrorBox msg={error} />}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
        <button style={btnSecondary} onClick={onBack} disabled={loading}>Back</button>
        <button
          style={{ ...btnPrimary, opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
          onClick={handlePay}
          disabled={loading || !stripe}
        >
          {loading ? 'Processing...' : `Pay ${selectedPlan.price}`}
        </button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px', alignItems: 'center' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={DIM} strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
        <span style={{ color: DIM, fontSize: '12px' }}>256-bit SSL encrypted</span>
      </div>
    </div>
  );
}

function PaymentStep(props) {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm {...props} />
    </Elements>
  );
}

/* ── Step 5: Success ── */
function SuccessStep({ slug, firstName }) {
  const [copied, setCopied] = useState(false);
  const profileUrl = `joinmane.com/${slug}`;
  const fullUrl = `https://${profileUrl}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{
        width: '80px', height: '80px', borderRadius: '50%', margin: '0 auto 24px',
        background: GOLD_DIM, border: `2px solid ${GOLD}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '36px',
      }}>
        🎉
      </div>
      <h1 style={{ fontWeight: 900, fontSize: '32px', marginBottom: '8px' }}>
        Welcome to Mane{' '}
        <span role="img" aria-label="celebration">🎉</span>
      </h1>
      <p style={{ color: MUTED, fontSize: '16px', marginBottom: '32px' }}>
        You're all set, {firstName}! Your professional profile is live.
      </p>

      {/* Profile URL card */}
      <div style={{
        background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '24px', marginBottom: '24px',
        display: 'inline-block', minWidth: '300px',
      }}>
        <p style={{ color: MUTED, fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '8px' }}>
          Your Profile URL
        </p>
        <p style={{ color: GOLD, fontSize: '20px', fontWeight: 900, letterSpacing: '0.02em', marginBottom: '16px' }}>
          {profileUrl}
        </p>
        <button
          onClick={handleCopy}
          style={{
            background: copied ? 'rgba(16,185,129,0.12)' : GOLD_DIM,
            border: `1px solid ${copied ? 'rgba(16,185,129,0.4)' : GOLD}`,
            color: copied ? '#10b981' : GOLD,
            fontWeight: 700, fontSize: '13px', padding: '12px 28px', borderRadius: '8px', cursor: 'pointer',
            letterSpacing: '0.06em', transition: 'all 0.2s',
          }}
        >
          {copied ? '✓ Copied!' : 'Copy Link'}
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '320px', margin: '0 auto' }}>
        <a
          href={fullUrl}
          style={{ ...btnPrimary, textDecoration: 'none', textAlign: 'center', display: 'block', borderRadius: '8px' }}
        >
          View Your Profile
        </a>
        <a
          href="/dash"
          style={{ ...btnSecondary, textDecoration: 'none', textAlign: 'center', display: 'block' }}
        >
          Go to Dashboard
        </a>
      </div>
    </div>
  );
}

/* ── Error display ── */
function ErrorBox({ msg }) {
  return (
    <div style={{
      background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
      color: '#ef4444', fontSize: '13px', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px',
    }}>
      {msg}
    </div>
  );
}

/* ── Main Join Component ── */
export default function Join() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', password: '', businessName: '', city: '',
    profession: '', yearsExp: '', bio: '', instagram: '',
    plan: 'pro',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Scroll to top on step change
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [step]);

  const onChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    setError('');
  }, []);

  const validateStep1 = () => {
    if (!form.firstName.trim()) return 'First name is required.';
    if (!form.lastName.trim()) return 'Last name is required.';
    if (!form.email.trim() || !form.email.includes('@')) return 'Valid email is required.';
    if (form.password.length < 8) return 'Password must be at least 8 characters.';
    if (!form.businessName.trim()) return 'Business name is required.';
    return null;
  };

  const validateStep2 = () => {
    if (!form.profession) return 'Please select your profession.';
    return null;
  };

  const goNext = () => {
    setError('');
    if (step === 0) {
      const err = validateStep1();
      if (err) return setError(err);
    }
    if (step === 1) {
      const err = validateStep2();
      if (err) return setError(err);
    }
    setStep(s => s + 1);
  };

  const goBack = () => { setError(''); setStep(s => s - 1); };

  const handleSubmit = async (paymentMethodId, pmError) => {
    if (pmError) return setError(pmError);
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/professionals/signup', { ...form, paymentMethodId });
      setResult(res.data);
      // Store token for auto-login
      if (res.data.token) {
        localStorage.setItem('fu_token', res.data.token);
        api.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
      }
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: BG, minHeight: '100vh', color: WHITE, fontFamily: "'Inter', sans-serif" }}>
      <Helmet>
        <title>Join Mane — Professional Onboarding</title>
        <meta name="description" content="Join Mane — the platform for service professionals. Zero commission booking, CRM, and marketing tools." />
        <meta property="og:title" content="Join Mane — Start Your Professional Profile" />
        <meta property="og:description" content="The platform for every service professional. Online booking, client management, and marketing — all in one place." />
        <meta property="og:image" content="https://joinmane.com/og-image-v8.png" />
      </Helmet>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 24px 0' }}>
        <ManeLogo size={36} showWordmark />
      </div>

      {/* Progress bar (hide on success) */}
      {step < 4 && <ProgressBar step={step} />}

      {/* Step content */}
      <div style={{ maxWidth: '640px', margin: '0 auto', padding: '40px 24px 80px' }}>
        {step === 0 && <AccountStep form={form} onChange={onChange} onNext={goNext} error={error} />}
        {step === 1 && <ProfileStep form={form} onChange={onChange} onNext={goNext} onBack={goBack} />}
        {step === 2 && <PlanStep form={form} onChange={onChange} onNext={goNext} onBack={goBack} />}
        {step === 3 && <PaymentStep form={form} onBack={goBack} onSubmit={handleSubmit} loading={loading} error={error} />}
        {step === 4 && <SuccessStep slug={result?.slug} firstName={form.firstName} />}
      </div>

      {/* Footer */}
      <footer style={{ borderTop: `1px solid ${BORDER}`, padding: '24px', textAlign: 'center' }}>
        <p style={{ color: DIM, fontSize: '11px', letterSpacing: '0.05em' }}>
          © {new Date().getFullYear()} MANE · Secure checkout powered by Stripe
        </p>
      </footer>
    </div>
  );
}
